import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LocalStorageViewer = ({ onDataChange }) => {
  const [selectedKey, setSelectedKey] = useState('companies');
  const [data, setData] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const storageKeys = [
    { key: 'companies', label: 'Companies', icon: 'Building2' },
    { key: 'users', label: 'Users', icon: 'Users' },
    { key: 'savedAssetData', label: 'Assets', icon: 'Package' },
    { key: 'savedWorkOrderData', label: 'Work Orders', icon: 'ClipboardList' }
  ];

  useEffect(() => {
    loadData();
  }, [selectedKey]);

  const loadData = () => {
    try {
      const stored = localStorage.getItem(selectedKey);
      if (stored) {
        setData(JSON.parse(stored));
      } else {
        setData(null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setData({ error: 'Invalid JSON data' });
    }
  };

  const handleCopyToClipboard = () => {
    if (data) {
      navigator.clipboard?.writeText(JSON.stringify(data, null, 2));
      alert('Data copied to clipboard!');
    }
  };

  const handleRefresh = () => {
    loadData();
    if (onDataChange) onDataChange();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">LocalStorage Data Viewer</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" iconName="RefreshCw" onClick={handleRefresh}>
            Refresh
          </Button>
          <Button variant="outline" size="sm" iconName="Copy" onClick={handleCopyToClipboard} disabled={!data}>
            Copy JSON
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {storageKeys?.map((item) => (
          <button
            key={item?.key}
            onClick={() => setSelectedKey(item?.key)}
            className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200 ${
              selectedKey === item?.key
                ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={item?.icon} size={20} />
            <span className="font-medium text-sm">{item?.label}</span>
          </button>
        ))}
      </div>
      <div className="bg-muted/30 rounded-lg border border-border overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="Code" size={18} className="text-muted-foreground" />
            <span className="font-mono text-sm font-medium text-foreground">{selectedKey}</span>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={18} />
          </button>
        </div>

        {isExpanded && (
          <div className="p-4 max-h-96 overflow-auto">
            {data ? (
              <pre className="font-mono text-xs text-foreground whitespace-pre-wrap break-words">
                {JSON.stringify(data, null, 2)}
              </pre>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Icon name="Database" size={48} className="mb-3 opacity-50" />
                <p className="text-sm">No data found for this key</p>
              </div>
            )}
          </div>
        )}
      </div>
      {data && Array.isArray(data) && (
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Info" size={18} className="text-primary" />
            <span className="font-medium text-sm text-foreground">Data Statistics</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            <div>
              <p className="text-xs text-muted-foreground">Total Records</p>
              <p className="text-lg font-bold text-foreground">{data?.length}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Data Size</p>
              <p className="text-lg font-bold text-foreground">
                {(new Blob([JSON.stringify(data)])?.size / 1024)?.toFixed(2)} KB
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalStorageViewer;